export const ROLES = [
  "superadmin",
  "owner",
  "branch_admin",
  "receptionist",
  "dentist",
  "specialist",
  "assistant",
  "billing",
  "auditor",
  "patient",
] as const;
export type Role = (typeof ROLES)[number];

export const STAFF_ROLES = ROLES.filter((r) => r !== "patient") as Exclude<Role, "patient">[];
export const SCHEDULING_ROLES: Role[] = ["superadmin", "owner", "branch_admin", "receptionist", "dentist", "specialist"];

export const APPOINTMENT_STATUSES = [
  "requested",
  "pending_confirmation",
  "confirmed",
  "arrived",
  "waiting",
  "in_treatment",
  "completed",
  "cancelled",
  "no_show",
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const ACTIVE_STATUSES: AppointmentStatus[] = [
  "requested",
  "pending_confirmation",
  "confirmed",
  "arrived",
  "waiting",
  "in_treatment",
];

export const STATUS_LABEL: Record<AppointmentStatus, string> = {
  requested: "Solicitado",
  pending_confirmation: "Pendiente de confirmación",
  confirmed: "Confirmado",
  arrived: "Llegó",
  waiting: "En espera",
  in_treatment: "En atención",
  completed: "Finalizado",
  cancelled: "Cancelado",
  no_show: "No asistió",
};

export const STATUS_TRANSITIONS: Record<AppointmentStatus, AppointmentStatus[]> = {
  requested: ["pending_confirmation", "confirmed", "cancelled"],
  pending_confirmation: ["confirmed", "cancelled", "no_show"],
  confirmed: ["arrived", "cancelled", "no_show"],
  arrived: ["waiting", "in_treatment", "cancelled"],
  waiting: ["in_treatment", "cancelled"],
  in_treatment: ["completed"],
  completed: [],
  cancelled: [],
  no_show: [],
};

export function canTransition(from: AppointmentStatus, to: AppointmentStatus) {
  return STATUS_TRANSITIONS[from].includes(to);
}

export type AppointmentChannel = "web" | "assistant" | "admin" | "whatsapp" | "patient_portal";

export interface Branch {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  placeholder: boolean;
}

export interface Practitioner {
  id: string;
  displayName: string;
  title: string;
  specialties: string[];
  license: string | null;
  serviceIds: string[];
  color: string;
  bookableOnline: boolean;
  placeholder: boolean;
  active: boolean;
}

export interface Chair {
  id: string;
  branchId: string;
  name: string;
  active: boolean;
}

export interface Service {
  id: string;
  name: string;
  family: TreatmentFamily;
  summary: string;
  durationMinutes: number;
  bufferMinutes: number;
  bookableOnline: boolean;
  requiresEvaluation: boolean;
  active: boolean;
}

export type TreatmentFamily =
  | "diagnostico"
  | "prevencion"
  | "estetica"
  | "restauradora"
  | "endodoncia"
  | "periodoncia"
  | "implantes"
  | "ortodoncia"
  | "odontopediatria"
  | "cirugia"
  | "urgencia";

/** Weekly working window of a practitioner in a chair, in clinic local minutes. */
export interface AvailabilityRule {
  id: string;
  practitionerId: string;
  chairId: string;
  branchId: string;
  weekday: number;
  startMinutes: number;
  endMinutes: number;
  validFrom: string | null;
  validTo: string | null;
}

export interface ScheduleException {
  id: string;
  date: string;
  practitionerId: string | null;
  kind: "holiday" | "absence" | "block";
  startMinutes: number | null;
  endMinutes: number | null;
  reason: string;
}

export interface BusyInterval {
  practitionerId: string | null;
  chairId: string | null;
  start: number;
  end: number;
}

export interface Slot {
  start: string;
  end: string;
  practitionerId: string;
  chairId: string;
  branchId: string;
}

export interface Appointment {
  id: string;
  tenantId: string;
  branchId: string;
  practitionerId: string;
  chairId: string;
  serviceId: string;
  serviceName: string;
  patientId: string;
  patientUid: string | null;
  patientName: string;
  start: string;
  end: string;
  status: AppointmentStatus;
  channel: AppointmentChannel;
  notes: string | null;
  lockIds: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface Patient {
  id: string;
  tenantId: string;
  uid: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  documentId: string | null;
  birthDate: string | null;
  createdAt: string;
}

export type RequestStatus = "new" | "proposed" | "booked" | "needs_human" | "dismissed";

export interface AssistantRequest {
  id: string;
  source: "web" | "whatsapp" | "email" | "admin";
  rawText: string;
  redactedText: string;
  contact: { name: string | null; phone: string | null; email: string | null };
  interpretation: AssistantInterpretation | null;
  model: string;
  status: RequestStatus;
  appointmentId: string | null;
  createdAt: string;
}

export type AssistantIntent = "book" | "reschedule" | "cancel" | "question" | "urgent" | "other";

export interface AssistantInterpretation {
  intent: AssistantIntent;
  serviceId: string | null;
  urgency: "none" | "soon" | "urgent";
  preferredDays: number[];
  timeOfDay: "morning" | "afternoon" | "any";
  dateFrom: string | null;
  dateTo: string | null;
  summary: string;
  needsHuman: boolean;
  confidence: number;
}
